@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  :root {
    /* Brand */
    --color-brand: #E8600A;
    --color-brand-hover: #C44D06;

    /* Surfaces */
    --color-bg: #FAFAF8;
    --color-surface: #FFFFFF;
    --color-surface-raised: #FFFFFF;

    /* Borders */
    --color-border: #E6E2DC;
    --color-border-strong: #D4CFC8;

    /* Text */
    --color-text: #1C1917;
    --color-text-muted: #78716C;
    --color-text-subtle: #A8A29E;

    /* Sidebar */
    --sidebar-width: 240px;
    --sidebar-collapsed-width: 60px;
  }

  * {
    border-color: theme('colors.stone.200');
    box-sizing: border-box;
  }

  html {
    font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  body {
    margin: 0;
    background-color: var(--color-bg);
    color: var(--color-text);
    font-size: 0.875rem;
    line-height: 1.5;
  }

  #root {
    min-height: 100svh;
  }

  /* Typography scale */
  h1 { @apply text-2xl font-semibold tracking-tight text-stone-900; }
  h2 { @apply text-xl font-semibold tracking-tight text-stone-900; }
  h3 { @apply text-base font-semibold text-stone-900; }
  h4 { @apply text-sm font-semibold text-stone-800; }

  /* Focus ring — consistent across all interactive elements */
  :focus-visible {
    @apply outline-none ring-2 ring-brand-500 ring-offset-1;
  }
}

@layer components {
  /* Card */
  .card {
    @apply bg-white rounded-lg border border-stone-200 shadow-card;
  }

  /* Page header */
  .page-header {
    @apply flex items-start justify-between gap-4 mb-6;
  }

  /* Section label */
  .section-label {
    @apply text-2xs font-semibold uppercase tracking-widest text-stone-400;
  }

  /* Status badge variants */
  .badge {
    @apply inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium;
  }
  .badge-success  { @apply bg-success-light text-success-dark; }
  .badge-warning  { @apply bg-warning-light text-warning-dark; }
  .badge-danger   { @apply bg-danger-light text-danger-dark; }
  .badge-info     { @apply bg-info-light text-info-dark; }
  .badge-neutral  { @apply bg-stone-100 text-stone-600; }
  .badge-brand    { @apply bg-brand-100 text-brand-700; }

  /* Divider */
  .divider { @apply h-px bg-stone-200 my-4; }

  /* Scrollable area */
  .scroll-y { @apply overflow-y-auto scrollbar-thin; }
}

@media print {
  /* Hide everything except the recipe card when printing */
  body > * { display: none !important; }
  #root > * { display: none !important; }
  .print\:block { display: block !important; }
  .print\:hidden { display: none !important; }

  .print-only {
    display: block !important;
    position: fixed;
    top: 0; left: 0;
    width: 100%;
    font-size: 13px;
    color: #1c1917;
  }
}

@layer utilities {
  /* Thin scrollbar for sidebar and overflow areas */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: theme('colors.stone.300') transparent;
  }
  .scrollbar-thin::-webkit-scrollbar { width: 4px; }
  .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: theme('colors.stone.300');
    border-radius: 9999px;
  }

  /* Truncate with tooltip support */
  .truncate-cell { @apply truncate max-w-0; }

  /* Animated shimmer for skeleton loading */
  .shimmer {
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 400% 100%;
    animation: shimmer 1.4s ease infinite;
  }

  @keyframes shimmer {
    0%   { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  /* Fade in */
  .fade-in {
    animation: fadeIn 0.15s ease-out;
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
}
